import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ParameterDetail } from "@/features/line-editor/hooks";
import { getParameterSourceFriendlyLabel } from "@/lib/display-labels";
import { LOSS_TYPE_LABELS, PRODUCTION_ROLE_LABELS } from "@/types/parameters.types";
import { formatQuantity } from "@/lib/quantity-format";

interface Props {
  counters: ParameterDetail[];
  lineUnit: string | null;
  isView: boolean;
  removePending: boolean;
  onEdit: (p: ParameterDetail) => void;
  onDelete: (p: ParameterDetail) => void;
}

/**
 * Spec 081 US1 — a resposta para "esta máquina está medindo o quê", que hoje não tem lugar em tela
 * nenhuma. Cada contador com sentido aparece com o produto, o sentido, a equivalência que vale e a
 * origem do dado.
 *
 * A equivalência SOBREPOSTA é marcada: herdada e sobreposta são estados diferentes, e uma lista
 * que mostra só o número não deixa ninguém descobrir que aquele contador é a exceção.
 */
export function CounterList({
  counters,
  lineUnit,
  isView,
  removePending,
  onEdit,
  onDelete,
}: Props) {
  if (counters.length === 0) return null;

  return (
    <section className="space-y-1.5">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        O que esta máquina mede
      </h4>
      <ul className="space-y-1.5">
        {counters.map((p) => (
          <CounterItem
            key={p.id}
            param={p}
            lineUnit={lineUnit}
            isView={isView}
            removePending={removePending}
            onEdit={() => onEdit(p)}
            onDelete={() => onDelete(p)}
          />
        ))}
      </ul>
    </section>
  );
}

interface ItemProps {
  param: ParameterDetail;
  lineUnit: string | null;
  isView: boolean;
  removePending: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function CounterItem({ param, lineUnit, isView, removePending, onEdit, onDelete }: ItemProps) {
  const role = param.productionRole;
  const factor = param.effectiveUnitFactor;
  return (
    <li className="space-y-1 rounded border px-2 py-1.5 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-medium">{param.name}</p>
            {role ? <RoleBadge role={role} /> : null}
          </div>
          <p className="truncate text-xs">
            {param.product ? param.product.name : "Sem produto declarado"}
            {role === "SCRAPS" && param.lossType ? ` · ${LOSS_TYPE_LABELS[param.lossType]}` : ""}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {getParameterSourceFriendlyLabel(param.source)}
            {param.unit ? ` · ${param.unit}` : ""}
          </p>
          <AccumulatedLine param={param} lineUnit={lineUnit} />
          {factor !== undefined ? (
            <p className="text-[11px] text-muted-foreground">
              1 {param.unit ?? "unidade"} = {factor} {lineUnit ?? "un"}
              {param.unitFactorIsOverride ? (
                <span className="ml-1 font-medium text-rd-warning-700">· equivalência própria</span>
              ) : (
                <span className="ml-1">· do produto</span>
              )}
            </p>
          ) : null}
        </div>
        {!isView ? (
          <div className="flex shrink-0 gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Editar ${param.name}`}
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Remover ${param.name}`}
              onClick={onDelete}
              disabled={removePending}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Spec 081 (FR-033a/FR-033b) — o acumulado do período, com o escopo dito por extenso.
 *
 * Nulo NÃO vira zero. Um contador recém-configurado que ainda não recebeu nada e um contador que
 * parou de receber produzem o mesmo zero; escrever "0" nos dois esconde o segundo, que é justamente
 * o caso que precisa aparecer. E um número sem dizer de qual execução veio não é informação.
 */
function AccumulatedLine({ param, lineUnit }: { param: ParameterDetail; lineUnit: string | null }) {
  const qty = param.accumulatedInLineUnit;
  if (qty === null || qty === undefined) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Sem execução de referência — nada acumulado ainda.
      </p>
    );
  }
  const scope = param.accumulatedScope === "last" ? "última execução" : "execução em curso";
  return (
    <p className="text-[11px]">
      <span className="font-medium">
        {formatQuantity(qty)} {lineUnit ?? "un"}
      </span>{" "}
      <span className="text-muted-foreground">na {scope}</span>
    </p>
  );
}

/** Sentido nunca só por cor: cor + ponto + rótulo, como manda o design system. */
function RoleBadge({ role }: { role: "PRODUCES" | "CONSUMES" | "SCRAPS" }) {
  const tone =
    role === "PRODUCES"
      ? "bg-rd-success-50 text-rd-success-700"
      : role === "SCRAPS"
        ? "bg-rd-danger-50 text-rd-danger-700"
        : "bg-muted text-muted-foreground";
  return (
    <span
      className={`border-current/20 inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-medium uppercase tracking-wider ${tone}`}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {PRODUCTION_ROLE_LABELS[role]}
    </span>
  );
}
